/**
 * The device id this browser presents to the API (X-Device-Id).
 *
 * A random value generated once and kept in a cookie on the site's parent
 * domain (the same scope as the API's `logged_in` marker), so every SPA of
 * the site — Polaris, Artemis — presents the SAME id for the session cookies
 * they share on the API host. The API hashes it into the session at login
 * (TOKEN_BINDING=device) and refuses the session from any request that does
 * not carry it as a header, on every transport.
 *
 * It is not auth state: it says nothing about whether a session exists, only
 * which browser this is. Where cookies are refused, no id is sent and the API
 * creates an unbound session rather than one this browser could not present
 * the id for later.
 */
const COOKIE_NAME = 'device_id';
const VALID = /^[A-Za-z0-9_-]{8,128}$/;
const MAX_AGE_SECONDS = 400 * 24 * 60 * 60; // the longest browsers allow

function readCookie(): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]+)'));
  return match ? match[1] : null;
}

/** `react-base-polaris-local.cedo.dev` -> `.cedo.dev`; bare hosts and IPs get a host-only cookie. */
function cookieDomain(): string | null {
  const host = window.location.hostname;
  if (!host.includes('.') || /^[\d.]+$/.test(host)) return null;
  return '.' + host.split('.').slice(-2).join('.');
}

function generate(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function getDeviceId(): string | null {
  try {
    const existing = readCookie();
    if (existing && VALID.test(existing)) return existing;

    const id = generate();
    const domain = cookieDomain();
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `${COOKIE_NAME}=${id}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}` +
      (domain ? `; domain=${domain}` : '');
    // Cookies disabled: the write is silently dropped, and we must not send an
    // id the next request could not repeat
    return readCookie() === id ? id : null;
  } catch {
    return null;
  }
}
